import { BaseMapController } from '../core/BaseMapController';
import { MarkerManager } from './MarkerManager';
import { LineManager } from './LineManager';
import { PolygonManager } from './PolygonManager';
import { HistoryManager } from '../history/HistoryManager';
import { IHistoryConfig, IHistoryManager, IMapCommand } from '../history/types';
import {
  AddMarkerCommand,
  RemoveMarkerCommand,
  UpdateMarkerCommand,
  AddLineCommand,
  RemoveLineCommand,
  UpdateLineCommand,
  AddPolygonCommand,
  RemovePolygonCommand,
  UpdatePolygonCommand,
  ClearAllCommand,
  BatchCommand,
  AddMarkerWithLineCommand,
  RemoveMarkerWithLineCommand,
  AddPolygonWithMarkersCommand,
  RemovePolygonWithMarkersCommand,
  AddPolygonCornerCommand,
  RemovePolygonCornerCommand,
  AddLineWithEndpointsCommand,
  RemoveLineWithEndpointsCommand,
  MoveMarkerCommand,
  MovePolygonCornerCommand,
  MoveLineEndpointCommand,
  AddLineSegmentCommand,
  RemoveLinePointCommand,
  RemovePointCommand,
} from '../history/commands';
import { IMarkerConfig, ILineConfig, IPolygonConfig, ICoordinate } from '../types';

export interface IHistoryFeatureManager {
  readonly historyManager: IHistoryManager;
  readonly isEnabled: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;

  enable(): void;
  disable(): void;
  clear(): void;

  addMarker(marker: IMarkerConfig): void;
  removeMarker(markerId: string): void;
  updateMarker(newMarker: IMarkerConfig): void;
  moveMarker(markerId: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate): void;

  addLine(line: ILineConfig): void;
  removeLine(lineId: string): void;
  updateLine(newLine: ILineConfig): void;

  addPolygon(polygon: IPolygonConfig): void;
  removePolygon(polygonId: string): void;
  updatePolygon(newPolygon: IPolygonConfig): void;

  clearAll(): void;
  executeBatch(commands: IMapCommand[], description?: string): void;

  addMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void;
  removeMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void;
  addPolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void;
  removePolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void;
  addPolygonCorner(marker: IMarkerConfig, polygon: IPolygonConfig): void;
  removePolygonCorner(
    marker: IMarkerConfig,
    oldPolygon: IPolygonConfig,
    newPolygon?: IPolygonConfig
  ): void;
  addLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void;
  removeLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void;

  undo(): boolean;
  redo(): boolean;

  getUndoDescription(): string | null;
  getRedoDescription(): string | null;
}

export class HistoryFeatureManager implements IHistoryFeatureManager {
  private mapController: BaseMapController;
  private markerManager: MarkerManager;
  private lineManager: LineManager;
  private polygonManager: PolygonManager;
  public readonly historyManager: HistoryManager;

  constructor(
    mapController: BaseMapController,
    markerManager: MarkerManager,
    lineManager: LineManager,
    polygonManager: PolygonManager,
    config: IHistoryConfig = {}
  ) {
    this.mapController = mapController;
    this.markerManager = markerManager;
    this.lineManager = lineManager;
    this.polygonManager = polygonManager;
    this.historyManager = new HistoryManager(config);
  }

  get isEnabled(): boolean {
    return this.historyManager.isEnabled;
  }

  get canUndo(): boolean {
    return this.historyManager.canUndo;
  }

  get canRedo(): boolean {
    return this.historyManager.canRedo;
  }

  enable(): void {
    this.historyManager.enable();
  }

  disable(): void {
    this.historyManager.disable();
  }

  clear(): void {
    this.historyManager.clear();
  }

  addMarker(marker: IMarkerConfig): void {
    const command = new AddMarkerCommand(this.markerManager, marker);
    this.historyManager.executeCommand(command);
  }

  removeMarker(markerId: string): void {
    const marker = this.markerManager.getMarkers().find(m => m.id === markerId);
    if (!marker) {
      console.warn(`Marker ${markerId} not found for removal`);
      return;
    }
    const command = new RemoveMarkerCommand(this.markerManager, marker);
    this.historyManager.executeCommand(command);
  }

  updateMarker(newMarker: IMarkerConfig): void {
    const previousMarker = this.markerManager.getMarkers().find(m => m.id === newMarker.id);
    if (!previousMarker) {
      console.warn(`Marker ${newMarker.id} not found for update`);
      return;
    }
    const command = new UpdateMarkerCommand(this.markerManager, newMarker, previousMarker);
    this.historyManager.executeCommand(command);
  }

  moveMarker(markerId: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate): void {
    const command = new MoveMarkerCommand(
      this.markerManager,
      markerId,
      oldCoordinate,
      newCoordinate
    );
    this.historyManager.executeCommand(command);
  }

  movePolygonCorner(
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    oldPolygon: IPolygonConfig,
    newPolygon: IPolygonConfig
  ): void {
    const command = new MovePolygonCornerCommand(
      this.markerManager,
      this.polygonManager,
      markerId,
      oldCoordinate,
      newCoordinate,
      oldPolygon,
      newPolygon
    );
    this.historyManager.executeCommand(command);
  }

  moveLineEndpoint(
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    affectedLines: Array<{ oldLine: ILineConfig; newLine: ILineConfig }>
  ): void {
    const command = new MoveLineEndpointCommand(
      this.markerManager,
      this.lineManager,
      markerId,
      oldCoordinate,
      newCoordinate,
      affectedLines
    );
    this.historyManager.executeCommand(command);
  }

  addLineSegment(
    marker: IMarkerConfig,
    newLines: ILineConfig[],
    removedLines?: ILineConfig[]
  ): void {
    const command = new AddLineSegmentCommand(
      this.markerManager,
      this.lineManager,
      marker,
      newLines,
      removedLines
    );
    this.historyManager.executeCommand(command);
  }

  addLine(line: ILineConfig): void {
    const command = new AddLineCommand(this.lineManager, line);
    this.historyManager.executeCommand(command);
  }

  removeLine(lineId: string): void {
    const line = this.lineManager.getLines().find(l => l.id === lineId);
    if (!line) {
      console.warn(`Line ${lineId} not found for removal`);
      return;
    }
    const command = new RemoveLineCommand(this.lineManager, line);
    this.historyManager.executeCommand(command);
  }

  updateLine(newLine: ILineConfig): void {
    const previousLine = this.lineManager.getLines().find(l => l.id === newLine.id);
    if (!previousLine) {
      console.warn(`Line ${newLine.id} not found for update`);
      return;
    }
    const command = new UpdateLineCommand(this.lineManager, newLine, previousLine);
    this.historyManager.executeCommand(command);
  }

  addPolygon(polygon: IPolygonConfig): void {
    const command = new AddPolygonCommand(this.polygonManager, polygon);
    this.historyManager.executeCommand(command);
  }

  removePolygon(polygonId: string): void {
    const polygon = this.polygonManager.getPolygons().find(p => p.id === polygonId);
    if (!polygon) {
      console.warn(`Polygon ${polygonId} not found for removal`);
      return;
    }
    const command = new RemovePolygonCommand(this.polygonManager, polygon);
    this.historyManager.executeCommand(command);
  }

  updatePolygon(newPolygon: IPolygonConfig): void {
    const previousPolygon = this.polygonManager.getPolygons().find(p => p.id === newPolygon.id);
    if (!previousPolygon) {
      console.warn(`Polygon ${newPolygon.id} not found for update`);
      return;
    }
    const command = new UpdatePolygonCommand(this.polygonManager, newPolygon, previousPolygon);
    this.historyManager.executeCommand(command);
  }

  clearAll(): void {
    const command = new ClearAllCommand(this.markerManager, this.lineManager, this.polygonManager);
    this.historyManager.executeCommand(command);
  }

  executeBatch(commands: IMapCommand[], description?: string): void {
    const batchCommand = new BatchCommand(commands, description);
    this.historyManager.executeCommand(batchCommand);
  }

  addMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void {
    const command = new AddMarkerWithLineCommand(
      this.markerManager,
      this.lineManager,
      marker,
      line
    );
    this.historyManager.executeCommand(command);
  }

  removeMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void {
    const command = new RemoveMarkerWithLineCommand(
      this.markerManager,
      this.lineManager,
      marker,
      line
    );
    this.historyManager.executeCommand(command);
  }

  addPolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void {
    const command = new AddPolygonWithMarkersCommand(
      this.markerManager,
      this.polygonManager,
      polygon,
      markers
    );
    this.historyManager.executeCommand(command);
  }

  removePolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void {
    const command = new RemovePolygonWithMarkersCommand(
      this.markerManager,
      this.polygonManager,
      polygon,
      markers
    );
    this.historyManager.executeCommand(command);
  }

  addPolygonCorner(marker: IMarkerConfig, polygon: IPolygonConfig): void {
    const command = new AddPolygonCornerCommand(
      this.markerManager,
      this.polygonManager,
      marker,
      polygon
    );
    this.historyManager.executeCommand(command);
  }

  removePolygonCorner(
    marker: IMarkerConfig,
    oldPolygon: IPolygonConfig,
    newPolygon?: IPolygonConfig
  ): void {
    const command = new RemovePolygonCornerCommand(
      this.markerManager,
      this.polygonManager,
      marker,
      oldPolygon,
      newPolygon
    );
    this.historyManager.executeCommand(command);
  }

  addLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void {
    const command = new AddLineWithEndpointsCommand(
      this.markerManager,
      this.lineManager,
      line,
      startMarker,
      endMarker
    );
    this.historyManager.executeCommand(command);
  }

  removeLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void {
    const command = new RemoveLineWithEndpointsCommand(
      this.markerManager,
      this.lineManager,
      line,
      startMarker,
      endMarker
    );
    this.historyManager.executeCommand(command);
  }

  removeLinePoint(
    marker: IMarkerConfig,
    removedLines: ILineConfig[],
    newLines?: ILineConfig[]
  ): void {
    const command = new RemoveLinePointCommand(
      this.markerManager,
      this.lineManager,
      marker,
      removedLines,
      newLines
    );
    this.historyManager.executeCommand(command);
  }

  removePoint(marker: IMarkerConfig): void {
    const command = new RemovePointCommand(this.markerManager, marker);
    this.historyManager.executeCommand(command);
  }

  undo(): boolean {
    return this.historyManager.undo();
  }

  redo(): boolean {
    return this.historyManager.redo();
  }

  getUndoDescription(): string | null {
    return this.historyManager.getUndoDescription();
  }

  getRedoDescription(): string | null {
    return this.historyManager.getRedoDescription();
  }
}
